import { PROJECT_INFO_URL_MAX_LENGTH } from '../constants/limits';
import { ProjectInfoData, ProjectInfoValueData } from '../dto/EntityRepository';
import { ProjectInfoType } from '../enum/EnumRepository';
import { isHostInDomains, parseHttpUrl } from './urlValidation';

export const PROJECT_INFO_TYPES: ProjectInfoType[] = [
  ProjectInfoType.ACTIVE_DATE,
  ProjectInfoType.LOCATION,
  ProjectInfoType.CREDIT,
  ProjectInfoType.OTHER,
];

export const createEmptyProjectInfoValue = (): ProjectInfoValueData => ({
  text: '',
  url: null,
  venueId: null,
});

export const withoutVenueIds = (values: ProjectInfoValueData[]): ProjectInfoValueData[] =>
  values.map((value) => (value.venueId == null ? value : { ...value, venueId: null }));

export const normalizeProjectInfoType = (
  type?: ProjectInfoType | string | null,
): ProjectInfoType =>
  PROJECT_INFO_TYPES.includes(type as ProjectInfoType)
    ? (type as ProjectInfoType)
    : ProjectInfoType.OTHER;

export const getProjectInfoTypeDefaultName = (type: ProjectInfoType): string | null => {
  if (type === ProjectInfoType.ACTIVE_DATE) return 'Active Date';
  if (type === ProjectInfoType.LOCATION) return 'Location';
  return null;
};

export const withProjectInfoType = (
  type: ProjectInfoType,
): Pick<ProjectInfoData, 'type' | 'customName' | 'customValues'> => {
  const nextType = normalizeProjectInfoType(type);
  return {
    type: nextType,
    customName: getProjectInfoTypeDefaultName(nextType),
    customValues: [],
  };
};

export const isSameProjectInfoValue = (
  prev: ProjectInfoValueData,
  next: ProjectInfoValueData,
): boolean =>
  prev.text === next.text &&
  prev.url === next.url &&
  prev.venueId === next.venueId;

export const isSameProjectInfoValues = (
  prev: ProjectInfoValueData[],
  next: ProjectInfoValueData[],
): boolean =>
  prev.length === next.length &&
  prev.every((value, index) => isSameProjectInfoValue(value, next[index]));

export const isSameProjectInfo = (prev: ProjectInfoData, next: ProjectInfoData): boolean =>
  prev.id === next.id &&
  prev.type === next.type &&
  prev.customName === next.customName &&
  isSameProjectInfoValues(prev.customValues, next.customValues);

export const appendProjectInfoValue = (
  values: ProjectInfoValueData[],
): ProjectInfoValueData[] => [...values, createEmptyProjectInfoValue()];

export const removeProjectInfoValueAt = (
  values: ProjectInfoValueData[],
  index: number,
): ProjectInfoValueData[] => values.filter((_, i) => i !== index);

export const moveProjectInfoValue = (
  values: ProjectInfoValueData[],
  from: number,
  to: number,
): ProjectInfoValueData[] => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= values.length ||
    to >= values.length
  ) {
    return values;
  }
  const next = [...values];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const patchProjectInfoValueAt = (
  type: ProjectInfoType,
  values: ProjectInfoValueData[],
  index: number,
  updates: Partial<ProjectInfoValueData>,
): ProjectInfoValueData[] =>
  values.map((value, i) => {
    if (i !== index) return value;
    const next: ProjectInfoValueData = { ...value, ...updates };
    if (type !== ProjectInfoType.LOCATION) {
      next.venueId = null;
    } else if (updates.venueId != null) {
      next.url = null;
    } else if (updates.url != null && updates.url !== '') {
      next.venueId = null;
    }
    return next;
  });

const toNullableUrl = (url: string | null): string | null => {
  if (url == null) return null;
  const trimmed = url.trim();
  return trimmed === '' ? null : trimmed;
};

export const toProjectInfoValueReq = (
  type: ProjectInfoType,
  value: ProjectInfoValueData,
): ProjectInfoValueData => {
  const url = toNullableUrl(value.url);
  const venueId =
    type === ProjectInfoType.LOCATION && value.venueId != null && value.venueId > 0
      ? value.venueId
      : null;
  if (venueId != null) {
    return { text: value.text, url: null, venueId };
  }
  return { text: value.text, url, venueId: null };
};

export const toProjectInfoValuesReq = (
  type: ProjectInfoType,
  values: ProjectInfoValueData[],
): ProjectInfoValueData[] => values.map((value) => toProjectInfoValueReq(type, value));

export const toProjectInfoCustomNameReq = (customName: string | null): string | null => {
  if (customName == null) return null;
  const trimmed = customName.trim();
  return trimmed === '' ? null : trimmed;
};

export const getProjectInfoDisplayName = (customName: string | null): string =>
  customName ?? '';

export const getProjectInfoDisplayText = (values: ProjectInfoValueData[]): string =>
  values.map((value) => value.text).filter((text) => text.trim() !== '').join(', ');

export const getProjectInfoPrimaryText = (values: ProjectInfoValueData[]): string =>
  values[0]?.text ?? '';

export const withProjectInfoPrimaryText = (
  type: ProjectInfoType,
  values: ProjectInfoValueData[],
  text: string,
): ProjectInfoValueData[] => {
  if (values.length === 0) {
    return [{ text, url: null, venueId: null }];
  }
  return patchProjectInfoValueAt(type, values, 0, { text });
};

export const isValidProjectInfoUrl = (url: string | null): boolean => {
  if (url == null) return true;
  const trimmed = url.trim();
  if (trimmed === '') return true;
  if (trimmed.length > PROJECT_INFO_URL_MAX_LENGTH) return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  const parsedUrl = parseHttpUrl(trimmed);
  return parsedUrl !== null && isHostInDomains(parsedUrl.hostname, [
    'architrave.work',
    'instagram.com',
    'behance.net',
    'vimeo.com',
  ]);
};
